import $ from 'jquery';

const COUNTRY_LIST_URL = 'https://hc.azure-api.net/v2/countrylist/static';

/**
 * The nationality field of the newsletter form (#web2lead,
 * partial/_webtolead.antlers.html): a text input with an autocomplete
 * combobox fed by the country list API. The request is only made when the
 * field is on the page — it used to be fired on every page load and thrown
 * away.
 */
export function initNationalityField() {
    const nationalityEl = document.getElementById('nationality');
    if (!nationalityEl) return;

    $.get(COUNTRY_LIST_URL, function (data) {
        initNationalityAutocomplete(nationalityEl, data);
    });
}

// Autocomplete combobox for the nationality field. `input` is the visible text input,
// `countries` is the countrylist payload ([{alpha_2_code, nationality}, ...]).
// The selected country's alpha_2_code is written to the sibling hidden input[name="country"].
function initNationalityAutocomplete(input, countries) {
    const wrapper = input.closest('.nationality-combobox');
    const hidden = wrapper ? wrapper.querySelector('input[type="hidden"][name="country"]') : null;
    const list = document.getElementById('nationality-list');
    if (!hidden || !list) return;

    // Diacritic-insensitive normalisation (e.g. "Österreich" matches "oster").
    const normalize = s => (s || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim();

    const items = countries
        .filter(c => c && c.nationality && c.alpha_2_code)
        .map(c => ({ code: c.alpha_2_code, name: c.nationality, norm: normalize(c.nationality) }))
        .sort((a, b) => a.name.localeCompare(b.name));

    let filtered = [];
    let highlighted = -1;

    // Text typed but no country resolved => invalid, so bad input can't submit.
    function setValidity() {
        if (input.value.trim() !== '' && hidden.value === '') {
            input.setCustomValidity(input.placeholder || 'Please select a valid option');
        } else {
            input.setCustomValidity('');
        }
    }

    function close() {
        list.hidden = true;
        list.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        highlighted = -1;
        filtered = [];
    }

    function select(item) {
        input.value = item.name;
        hidden.value = item.code;
        setValidity();
        close();
    }

    function renderHighlight() {
        Array.prototype.forEach.call(list.children, (li, i) => {
            const on = i === highlighted;
            li.classList.toggle('is-highlighted', on);
            li.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        if (highlighted >= 0 && list.children[highlighted]) {
            list.children[highlighted].scrollIntoView({ block: 'nearest' });
        }
    }

    function open(query) {
        const q = normalize(query);
        filtered = q === '' ? items.slice() : items.filter(it => it.norm.indexOf(q) !== -1);
        list.innerHTML = '';
        if (filtered.length === 0) {
            close();
            return;
        }
        filtered.forEach((it, i) => {
            const li = document.createElement('li');
            li.className = 'nationality-option';
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', 'false');
            li.id = 'nationality-option-' + i;
            li.textContent = it.name;
            // mousedown (not click) fires before the input's blur, so we don't lose focus first.
            li.addEventListener('mousedown', function (e) {
                e.preventDefault();
                select(it);
            });
            list.appendChild(li);
        });
        highlighted = -1;
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
    }

    input.addEventListener('input', function () {
        hidden.value = ''; // typing invalidates a previously resolved country
        open(input.value);
        setValidity();
    });

    input.addEventListener('focus', function () {
        if (input.value.trim() === '' || hidden.value === '') {
            open(input.value);
        }
    });

    input.addEventListener('keydown', function (e) {
        if (list.hidden && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            open(input.value);
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (filtered.length) { highlighted = (highlighted + 1) % filtered.length; renderHighlight(); }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (filtered.length) { highlighted = (highlighted - 1 + filtered.length) % filtered.length; renderHighlight(); }
                break;
            case 'Enter':
                if (!list.hidden && highlighted >= 0 && filtered[highlighted]) {
                    e.preventDefault();
                    select(filtered[highlighted]);
                }
                break;
            case 'Escape':
                close();
                break;
        }
    });

    input.addEventListener('blur', function () {
        // Resolve an exact (diacritic-insensitive) match; otherwise clear the country.
        const match = items.find(it => it.norm === normalize(input.value));
        if (match) {
            input.value = match.name;
            hidden.value = match.code;
        } else {
            hidden.value = '';
        }
        setValidity();
        close();
    });
}
